import React from 'react';

import { Property } from '@flipdish/orgmanagement';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import DeliveryDiningOutlinedIcon from '@mui/icons-material/DeliveryDiningOutlined';
import EditLocationOutlinedIcon from '@mui/icons-material/EditLocationOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import DevicesIcon from '@mui/icons-material/PhonelinkSetup';
import PinDropOutlinedIcon from '@mui/icons-material/PinDropOutlined';
import SettingsApplicationsOutlinedIcon from '@mui/icons-material/SettingsApplicationsOutlined';
import StoreOutlined from '@mui/icons-material/StoreOutlined';
import TvOutlinedIcon from '@mui/icons-material/TvOutlined';
import ViewModuleOutlinedIcon from '@mui/icons-material/ViewModuleOutlined';
import Warning from '@mui/icons-material/Warning';

import { type SettingsMenuItem } from '@fd/ui/List/SettingsList';

import { toKebabCase } from '../../helpers/strings';
import { phoneRegExp } from '../../helpers/validation';
import { TranslateFunction } from '../../overrides/react-localize-redux';
import { PortalViewId } from '../../viewConfiguration';
import { KioskIcon } from '../Kiosks/Components/KioskNestedMenuIcon';
import { getPhoneNumbersMenuItem } from '../PhoneAgent/phoneNumbersPage';
import { RMSModules } from '../RMS/utils/rmsModules';
import { propertiesPermissions } from './propertiesPermissions';

const PROPERTY_NAME_MAX_LENGTH = 100;
const PROPERTY_EMAIL_MAX_LENGTH = 100;
const PROPERTY_PHONE_MAX_LENGTH = 20;

export const validatePropertyForm = (
  values: any,
  translate: TranslateFunction,
  { isPhoneNumberEditable = true }: { isPhoneNumberEditable?: boolean } = {}
) => {
  const errors: any = {};

  if (!values.name) {
    errors.name = translate('Required');
  } else if (values.name.length > PROPERTY_NAME_MAX_LENGTH) {
    errors.name = translate('Max_fieldname_length_exceeded', {
      fieldName: 'Property Name',
      max: PROPERTY_NAME_MAX_LENGTH,
    });
  }

  if (!values.countryCode || values.countryCode === '') {
    errors.countryCode = translate('Required');
  }

  if (!values.emailAddress) {
    errors.emailAddress = translate('Required');
  } else if (values.emailAddress.length > PROPERTY_EMAIL_MAX_LENGTH) {
    errors.emailAddress = translate('Max_fieldname_length_exceeded', {
      fieldName: 'Email',
      max: PROPERTY_EMAIL_MAX_LENGTH,
    });
  } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.emailAddress)) {
    errors.emailAddress = translate('Invalid_email');
  }

  if (!isPhoneNumberEditable) {
    return errors;
  }

  if (values.phoneNumber.length > PROPERTY_PHONE_MAX_LENGTH) {
    errors.phoneNumber = translate('Max_fieldname_length_exceeded', {
      fieldName: 'Phone Number',
      max: PROPERTY_PHONE_MAX_LENGTH,
    });
  }
  if (values.phoneNumber && !phoneRegExp.test(values.phoneNumber)) {
    errors.phoneNumber = translate('phone_invalid_input');
  }

  return errors;
};

export const getPropertySettingsMenuItems = ({
  appId,
  property,
  isSearchItems = false,
  isShowCollectionSettingsAtPropertyLevelOn = false,
  isPhoneNumbersPageOn = false,
}: {
  appId: string;
  property: Property;
  isSearchItems?: boolean;
  isShowCollectionSettingsAtPropertyLevelOn?: boolean;
  isPhoneNumbersPageOn?: boolean;
}): SettingsMenuItem[] => {
  const basePropertyUrl = `/${appId}/properties/${property?.propertyId}`;

  const baseItem = {
    propertyId: property?.propertyId ?? '',
    propertyName: property?.name ?? '',
  };
  const menuItems: SettingsMenuItem[] = [
    {
      title: 'General_settings',
      path: `${basePropertyUrl}/general-settings`,
      icon: <MapOutlinedIcon />,
      subtitle: isPhoneNumbersPageOn
        ? 'Set_address_email_and_map_pin'
        : 'Set_address_phone_email_and_map_pin',
      permissions: propertiesPermissions,
      searchAlias: [
        'Property name',
        'Restaurant name',
        'Set address',
        ...(isPhoneNumbersPageOn ? [] : ['Phone number']),
        'Email',
        'Map pin',
        'property timezone',
        'property type',
      ],
      ...baseItem,
    },
    ...(isPhoneNumbersPageOn
      ? [getPhoneNumbersMenuItem({ appId, permissions: propertiesPermissions, ...baseItem })]
      : []),
    {
      title: 'Opening_Hours',
      path: `${basePropertyUrl}/opening-hours`,
      icon: <AccessTimeOutlinedIcon />,
      subtitle: 'Configure_property_opening_hours',
      viewId: PortalViewId.PropertiesOpeningHours,
      permissions: propertiesPermissions,
      searchAlias: [
        'Delivery hours',
        'Collection hours',
        'Set opening hours',
        'Update opening hours',
        'Property opening hours',
        'Restaurant opening hours',
      ],
      ...baseItem,
    },
    {
      title: 'Collection_Settings',
      path: `${basePropertyUrl}/settings/collection`,
      icon: <StoreOutlined />,
      subtitle: 'Enable_disable_collection_and_set_minimum_price',
      permissions: propertiesPermissions,
      searchAlias: [], // TADHG - TBC, Check with product
      ...baseItem,
    },
    {
      title: 'Operating_Configuration',
      path: `${basePropertyUrl}/${toKebabCase(RMSModules.OPERATING_CONFIGURATION)}`,
      icon: <SettingsApplicationsOutlinedIcon />,
      subtitle: 'Sms_configuration_staff_meals_emails_for_staff_com',
      permissions: propertiesPermissions,
      searchAlias: [
        'Staff meal limits',
        'Email staff clockout',
        'Email for purchase orders',
        'SMS configuration',
        'Order status update message template',
        'Timeout for slow internet connection',
      ],
      ...baseItem,
    },
    {
      title: 'Layout_Configuration',
      path: `${basePropertyUrl}/layout`,
      icon: <ViewModuleOutlinedIcon />,
      permissions: ['EditStores'],
      subtitle: 'Configure_restaurant_layout',
      searchAlias: [
        'Table plan',
        'Restaurant tables',
        'Table list',
        'Table layout designer',
        'Manage zones',
        'Add new zone',
        'Add tables',
        'Edit seating plan',
        'seating layout',
      ],
      ...baseItem,
    },
    {
      title: 'Geo_Configuration',
      path: `${basePropertyUrl}/${toKebabCase(RMSModules.GEO_CONFIGURATION)}`,
      icon: <PinDropOutlinedIcon />,
      permissions: ['EditStores'],
      subtitle: 'Set_amenities_co_ordinates_and_ratings',
      searchAlias: ['Amenities', 'latitude', 'longitude'],
      ...baseItem,
    },
    {
      title: 'Address_Mapping',
      path: `${basePropertyUrl}/${toKebabCase(RMSModules.ADDRESS_MAPPING)}`,
      icon: <EditLocationOutlinedIcon />,
      permissions: ['EditStores'],
      subtitle: 'Configure_property_address_overrides',
      searchAlias: ['Area codes', 'Area names', 'Address mappings'],
      ...baseItem,
    },
    {
      title: 'Delivery_Management_Configuration',
      path: `${basePropertyUrl}/${toKebabCase(RMSModules.DELIVERY_CONFIGURATION)}`,
      icon: <DeliveryDiningOutlinedIcon />,
      permissions: ['FlipdishStaff'],
      tagText: 'Flipdish_staff_only',
      subtitle: 'Set_type_webhook_and_API_key',
      ...baseItem,
    },
    {
      title: 'Float_Configuration',
      path: `${basePropertyUrl}/${toKebabCase(RMSModules.FLOAT_CONFIGURATION)}`,
      icon: <TvOutlinedIcon />,
      subtitle: 'Configure_property_float_settings',
      permissions: propertiesPermissions,
      searchAlias: [
        'Setup float',
        'Petty cash expense',
        'Float duration',
        'Float account',
        'Closing screen',
        'Enable cash count',
        'Cash count',
        'End of day cash up',
      ],
      ...baseItem,
    },
    {
      title: 'Localization_Configuration',
      path: `${basePropertyUrl}/localisation-configuration`,
      icon: <LanguageOutlinedIcon />,
      permissions: ['EditStores'],
      subtitle: 'Configure_localization_setting_overrides',
      searchAlias: [
        'POS Vat %',
        'POS receipt print units',
        'POS Vat settings',
        'POS vat applicable',
        'POS vat inclusive',
        'POS vat percentage',
        'vat caption',
        'point of sale VAT',
      ],
      ...baseItem,
    },
    {
      title: 'Danger_zone_title',
      path: `${basePropertyUrl}/dangerzone`,
      icon: <Warning />,
      permissions: ['Owner', 'FlipdishStaff'],
      subtitle: 'property_danger_zone_description',
      isDangerZone: true,
      searchAlias: ['archive property', 'unarchive property'],
      hidden: isSearchItems,
      ...baseItem,
    },
  ];

  if (!isShowCollectionSettingsAtPropertyLevelOn) {
    return menuItems.filter((item) => item.title !== 'Collection_Settings');
  }

  return menuItems;
};

export const getPropertyDevicesMenuItems = ({
  appId,
  property,
  hasBaseSubscription,
  loadAllItemsForProperty = true,
}: {
  appId: string;
  property: Property;
  hasBaseSubscription: boolean;
  loadAllItemsForProperty?: boolean;
}): SettingsMenuItem[] => {
  const baseItem = {
    propertyId: property?.propertyId ?? '',
    propertyName: property?.name ?? '',
  };

  const kdsItem = {
    title: 'KDS_POS_and_Others' as TranslationId,
    subtitle: 'View_and_setup_Kitchen_Display_Screen_POS_and_othe' as TranslationId,
    path: `/${appId}/properties/${property?.propertyId}/devices`,
    icon: <TvOutlinedIcon />,
    permissions: ['Owner', 'ManagedOwner', 'FlipdishStaff'] as AppState['permissions'],
    ...baseItem,
  };
  // terminals and kiosk aren't at property level and are already loaded
  if (!loadAllItemsForProperty) {
    return hasBaseSubscription ? [kdsItem] : [];
  }
  const menuItems: SettingsMenuItem[] = [
    {
      title: 'Terminals',
      subtitle: 'View_and_setup_your_kitchen_terminals',
      path: `/${appId}/terminals`,
      icon: <DevicesIcon />,
      permissions: ['ViewHydraConfig', 'Owner', 'ManagedOwner', 'FlipdishStaff'],
    },
    {
      title: 'Kiosk',
      subtitle: 'View_your_Kiosk_devices_here',
      path: `/${appId}/sales-channels/kiosks`,
      icon: <KioskIcon />,
      permissions: ['Owner', 'ManagedOwner', 'FlipdishStaff'],
    },
  ];

  if (hasBaseSubscription) {
    menuItems.splice(1, 0, kdsItem);
  }

  return menuItems;
};
